/**
 * Lean coach turn context — never include candles/bars.
 */

import { compactNestedComposerFields } from "./strategyRdComposer";
import {
  strategyRdEntryModeLabel,
  strategyRdKnownConceptLabel,
} from "./strategyRdConceptCopy";
import type {
  StrategyRdComposerConfluenceSlot,
  StrategyRdComposerEntrySlot,
  StrategyRdComposerSetupSlot,
  StrategyRdLogicPath,
  StrategyRdOhlcStats,
  StrategyRdOutcomeSummary,
} from "../types/strategyRd";
import type {
  StrategyRdCoachComposerBody,
  StrategyRdCoachComposerPatch,
  StrategyRdCoachFocus,
  StrategyRdCoachHistoryMessage,
  StrategyRdCoachScanSummary,
  StrategyRdCoachTurnRequest,
  StrategyRdCoachUiMessage,
} from "../types/strategyRdCoach";
import { STRATEGY_RD_COACH_HISTORY_LIMIT } from "../types/strategyRdCoach";

const CANDLE_KEYS = new Set([
  "candles",
  "bars",
  "ohlc",
  "candle_data",
  "candles_by_tf",
  "match_markers",
  "sr_rails",
]);

export function stripCoachCandleKeys<T>(value: T, depth = 0): T {
  if (depth > 12 || value == null) return value;
  if (Array.isArray(value)) {
    if (
      value.length > 50 &&
      value.slice(0, 3).every((x) => typeof x === "number")
    ) {
      return null as T;
    }
    return value.map((item) => stripCoachCandleKeys(item, depth + 1)) as T;
  }
  if (typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (CANDLE_KEYS.has(k.toLowerCase())) continue;
      out[k] = stripCoachCandleKeys(v, depth + 1);
    }
    return out as T;
  }
  return value;
}

export function trimCoachHistory(
  messages: StrategyRdCoachUiMessage[] | StrategyRdCoachHistoryMessage[],
  limit = STRATEGY_RD_COACH_HISTORY_LIMIT
): StrategyRdCoachHistoryMessage[] {
  const mapped = messages
    .filter((m) => m.role === "user" || m.role === "assistant")
    .map((m) => ({
      role: m.role,
      content: String(m.content || "").slice(0, 1500),
    }));
  return mapped.slice(-Math.max(0, limit));
}

export function buildCoachComposerPayload(args: {
  candleType: string;
  setup: StrategyRdComposerSetupSlot;
  confluences: StrategyRdComposerConfluenceSlot[];
  entry: StrategyRdComposerEntrySlot;
  sessionIds?: string[];
}): StrategyRdCoachComposerBody {
  const nested = compactNestedComposerFields({
    setup: args.setup,
    confluences: args.confluences,
    entry: args.entry,
  });
  return stripCoachCandleKeys({
    candle_type: args.candleType,
    setup: nested.setup,
    confluences: nested.confluences,
    entry: nested.entry,
    session_ids: args.sessionIds?.length ? [...args.sessionIds] : undefined,
  }) as StrategyRdCoachComposerBody;
}

/** Funnel fields BE/TES need for explain_scan — empty `{}` does not count. */
const COACH_OHLC_FUNNEL_KEYS = [
  "bars_scanned",
  "bars_expected",
  "setup_match_count",
  "after_filter_count",
  "match_count",
  "coverage_ratio",
  "detect_profile",
  "candle_type",
  "lookback_days",
  "confluences",
] as const;

export function hasUsableCoachOhlcStats(
  stats: StrategyRdOhlcStats | Record<string, unknown> | null | undefined
): boolean {
  if (!stats || typeof stats !== "object") return false;
  return COACH_OHLC_FUNNEL_KEYS.some((key) => {
    const v = (stats as Record<string, unknown>)[key];
    if (v == null) return false;
    if (typeof v === "number") return Number.isFinite(v);
    if (typeof v === "string") return v.trim().length > 0;
    if (typeof v === "object") return Object.keys(v as object).length > 0;
    return true;
  });
}

export function leanCoachOhlcStats(
  stats: StrategyRdOhlcStats | Record<string, unknown> | null | undefined
): StrategyRdOhlcStats | null {
  if (!hasUsableCoachOhlcStats(stats)) return null;
  const src = stats as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const key of COACH_OHLC_FUNNEL_KEYS) {
    if (src[key] != null) out[key] = src[key];
  }
  // Preserve common diagnostic extras when present (no candles).
  for (const key of [
    "md_source",
    "market_data_broker",
    "window_start",
    "window_end",
    "status",
    "volume_available",
    "volume_status",
  ] as const) {
    if (src[key] != null) out[key] = src[key];
  }
  return stripCoachCandleKeys(out) as StrategyRdOhlcStats;
}

export function buildCoachScanSummary(args: {
  ohlcStats?: StrategyRdOhlcStats | Record<string, unknown> | null;
  logicPath?: StrategyRdLogicPath | Record<string, unknown> | null;
}): StrategyRdCoachScanSummary | null {
  const ohlc = leanCoachOhlcStats(args.ohlcStats);
  const logic =
    args.logicPath && typeof args.logicPath === "object"
      ? stripCoachCandleKeys(args.logicPath)
      : null;
  if (!ohlc && !logic) return null;
  return {
    ...(ohlc ? { ohlc_stats: ohlc } : {}),
    ...(logic ? { logic_path: logic as StrategyRdLogicPath } : {}),
  };
}

/**
 * Infer turn goal from free text when chips did not set an override.
 * Prefer why_no_hits when the user asks about zero/few matches.
 */
export function inferCoachTurnGoal(
  message: string,
  fallback: string | null | undefined = "build"
): string {
  const text = message.trim().toLowerCase();
  if (!text) return fallback || "build";

  const whyNoHits =
    /\b(no|zero|0|few)\s+(hits?|matches?)\b/.test(text) ||
    /\bwhy\s+(did|do|are|is).{0,40}\b(no|zero|few|0)\b/.test(text) ||
    /\bwhy\s+no\s+(hits?|matches?)\b/.test(text) ||
    /\b(empty|nothing)\s+(preview|scan|matches?|hits?)\b/.test(text);
  if (whyNoHits) return "why_no_hits";

  if (
    /\bexplain\b/.test(text) ||
    /\bwhat\s+(does|do|is)\b/.test(text) ||
    /\bhow\s+(does|do|should)\b/.test(text)
  ) {
    return "explain";
  }

  if (/\bimprov/.test(text) || /\bloosen\b/.test(text)) return "improve";

  return fallback || "build";
}

export function buildCoachTurnRequest(args: {
  message: string;
  goal?: string | null;
  composer?: StrategyRdCoachComposerBody | null;
  scanSummary?: StrategyRdCoachScanSummary | null;
  outcomeSummary?: StrategyRdOutcomeSummary | Record<string, unknown> | null;
  history: StrategyRdCoachUiMessage[] | StrategyRdCoachHistoryMessage[];
  sessionId?: string | null;
  focus?: StrategyRdCoachFocus | null;
  /** The Lab's whole state (§10): open hypothesis, template and sizing incl. unsaved edits. */
  hypothesisId?: string | null;
  template?: Record<string, unknown> | null;
  sizing?: Record<string, unknown> | null;
  images?: StrategyRdCoachTurnRequest["images"];
  chartFocus?: StrategyRdCoachTurnRequest["chart_focus"];
  market?: StrategyRdCoachTurnRequest["market"];
}): StrategyRdCoachTurnRequest {
  const history = trimCoachHistory(args.history);
  return {
    ...(args.market ? { market: args.market } : {}),
    ...(args.hypothesisId ? { hypothesis_id: args.hypothesisId } : {}),
    ...(args.template ? { template: args.template } : {}),
    ...(args.sizing ? { sizing: args.sizing } : {}),
    ...(args.images && args.images.length > 0 ? { images: args.images } : {}),
    ...(args.chartFocus ? { chart_focus: args.chartFocus } : {}),
    message: args.message.trim().slice(0, 4000),
    goal: args.goal || undefined,
    composer: args.composer ? stripCoachCandleKeys(args.composer) : undefined,
    scan_summary: args.scanSummary
      ? stripCoachCandleKeys(args.scanSummary)
      : undefined,
    outcome_summary: args.outcomeSummary
      ? stripCoachCandleKeys(args.outcomeSummary)
      : undefined,
    history: history.length > 0 ? history : undefined,
    session_id: args.sessionId || undefined,
    focus: args.focus || undefined,
  };
}

export function parseCoachComposerSlots(composer: StrategyRdCoachComposerBody | Record<string, unknown> | null | undefined): {
  setup: StrategyRdComposerSetupSlot;
  confluences: StrategyRdComposerConfluenceSlot[];
  entry: StrategyRdComposerEntrySlot;
  candleType?: string | null;
  sessionIds?: string[] | null;
  /** N of M (`set_optional_min`). */
  optionalMin?: number | null;
} | null {
  if (!composer || typeof composer !== "object") return null;
  const setupRaw = (composer as StrategyRdCoachComposerBody).setup;
  if (!setupRaw || typeof setupRaw !== "object") return null;
  const setup = setupRaw as StrategyRdComposerSetupSlot;
  if (!setup.id) return null;

  const confRaw = (composer as StrategyRdCoachComposerBody).confluences;
  const confluences: StrategyRdComposerConfluenceSlot[] = [];
  if (Array.isArray(confRaw)) {
    for (const item of confRaw) {
      if (typeof item === "string" && item.trim()) {
        confluences.push({ id: item.trim() });
      } else if (item && typeof item === "object" && "id" in item) {
        confluences.push(item as StrategyRdComposerConfluenceSlot);
      }
    }
  }

  const entryRaw = (composer as StrategyRdCoachComposerBody).entry;
  const entry: StrategyRdComposerEntrySlot =
    entryRaw && typeof entryRaw === "object"
      ? (entryRaw as StrategyRdComposerEntrySlot)
      : { mode: "break_close" };

  const sessionIds = Array.isArray((composer as StrategyRdCoachComposerBody).session_ids)
    ? ((composer as StrategyRdCoachComposerBody).session_ids as string[])
    : null;

  const optionalMinRaw = Number((composer as Record<string, unknown>).optional_min);
  return {
    setup,
    confluences,
    entry,
    candleType:
      typeof (composer as StrategyRdCoachComposerBody).candle_type === "string"
        ? String((composer as StrategyRdCoachComposerBody).candle_type)
        : null,
    sessionIds,
    optionalMin: Number.isFinite(optionalMinRaw) && optionalMinRaw >= 1 ? Math.floor(optionalMinRaw) : null,
  };
}

function asPatchRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

/**
 * TES often nests the payload (`set_entry.entry.mode`). Validate-patch reads
 * `mode` / `id` on the op itself, so lift those fields before the request.
 */
export function flattenCoachPatchOps(
  patch: StrategyRdCoachComposerPatch | null | undefined
): StrategyRdCoachComposerPatch {
  const ops = patch?.ops;
  if (!Array.isArray(ops)) return { ops: [] };
  return {
    ops: ops.map((raw) => {
      const op = String(raw.op || "");
      if (op === "set_entry") {
        const nested = asPatchRecord(raw.entry);
        if (!nested) return raw;
        return {
          ...raw,
          mode: raw.mode ?? nested.mode,
          side: raw.side ?? nested.side,
          candle_type: raw.candle_type ?? nested.candle_type,
          side_policy: raw.side_policy ?? nested.side_policy,
        };
      }
      if (op === "set_setup") {
        const nested = asPatchRecord(raw.setup);
        if (!nested) return raw;
        return {
          ...raw,
          id: raw.id ?? nested.id,
          polarity: raw.polarity ?? nested.polarity,
          side: raw.side ?? nested.side,
          params: raw.params ?? nested.params,
          candle_type: raw.candle_type ?? nested.candle_type,
        };
      }
      if (op === "add_confluence" || op === "remove_confluence") {
        const nested = asPatchRecord(raw.confluence);
        if (!nested) return raw;
        return { ...raw, id: raw.id ?? nested.id };
      }
      return raw;
    }),
  };
}

/** Stamp entry fields from the patch onto slots the validator echoed unchanged. */
export function applyCoachEntryPatchToSlots<
  T extends { entry: StrategyRdComposerEntrySlot },
>(slots: T, patch: StrategyRdCoachComposerPatch | null | undefined): T {
  const flat = flattenCoachPatchOps(patch);
  let entry = slots.entry;
  let changed = false;
  for (const raw of flat.ops) {
    if (String(raw.op || "") !== "set_entry") continue;
    const next: StrategyRdComposerEntrySlot = { ...entry };
    if (typeof raw.mode === "string" && raw.mode.trim()) {
      next.mode = raw.mode.trim();
      changed = true;
    }
    if (typeof raw.side === "string" && raw.side.trim()) {
      next.side = raw.side.trim();
      changed = true;
    }
    if (typeof raw.candle_type === "string" && raw.candle_type.trim()) {
      next.candle_type = raw.candle_type.trim();
      changed = true;
    }
    entry = next;
  }
  return changed ? { ...slots, entry } : slots;
}

const PATCH_OP_LABELS: Record<string, string> = {
  set_setup: "Setup",
  set_entry: "Entry",
  add_confluence: "Add confluence",
  remove_confluence: "Remove confluence",
  set_confluence: "Confluence",
  update_confluence: "Confluence",
  set_sessions: "Sessions",
  set_session_ids: "Sessions",
  set_confluence_options: "Confluence options",
  set_optional_min: "At least N optional confluences",
};

const PATCH_SIDE_LABELS: Record<string, string> = {
  both: "Both",
  long: "Long",
  short: "Short",
  bull: "Bullish",
  bear: "Bearish",
};

function prettyPatchToken(key: string, value: string): string {
  const token = value.trim();
  if (!token) return "";
  if (key === "mode" || key === "entry_mode") return strategyRdEntryModeLabel(token);
  if (key === "side" || key === "polarity") return PATCH_SIDE_LABELS[token] || token;
  if (key === "id" || key === "concept_id" || key === "setup_id") {
    return strategyRdKnownConceptLabel(token) || strategyRdEntryModeLabel(token);
  }
  if (token.includes("_")) {
    return strategyRdKnownConceptLabel(token) || strategyRdEntryModeLabel(token);
  }
  return token;
}

function prettyPatchValue(key: string, value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number") {
    return prettyPatchToken(key, String(value));
  }
  if (Array.isArray(value)) {
    return value
      .map((item) => prettyPatchValue(key, item))
      .filter(Boolean)
      .join(", ");
  }
  if (typeof value === "object") {
    const rec = value as Record<string, unknown>;
    const order = ["id", "mode", "polarity", "side", "candle_type"];
    const keys = order.filter((k) => rec[k] != null).concat(
      Object.keys(rec).filter((k) => order.indexOf(k) < 0 && k !== "op")
    );
    return keys
      .map((k) => prettyPatchValue(k, rec[k]))
      .filter(Boolean)
      .join(" · ");
  }
  return "";
}

export function formatCoachPatchOpsSummary(
  patch: { ops?: Array<{ op?: string; [key: string]: unknown }> } | null | undefined
): string[] {
  const ops = patch?.ops;
  if (!Array.isArray(ops) || ops.length === 0) return [];
  return ops.map((op) => {
    const label = PATCH_OP_LABELS[String(op.op || "")] || "Change";
    const detail = Object.entries(op)
      .filter(([k]) => k !== "op")
      .map(([k, v]) => prettyPatchValue(k, v))
      .filter(Boolean)
      .join(" · ");
    return detail ? `${label}: ${detail}` : label;
  });
}

export function isCoachQuotaSlug(slug: string | null | undefined): boolean {
  if (!slug) return false;
  return (
    slug === "strategy_rd.coach.quota.weekly_turns" ||
    slug === "strategy_rd.coach.quota.weekly_tokens"
  );
}

export function isCoachThreadNotFoundSlug(slug: string | null | undefined): boolean {
  return slug === "strategy_rd.coach.thread_not_found";
}
